import type { CSSProperties, KeyboardEvent, ReactNode } from "react";
import clsx from "clsx";

import { dataAttributes, type DataAttributes } from "../../data-attributes";

import styles from "./card.module.css";

/**
 * SR-301 phase 8 B7/B8. `Card` used to take a single opaque `children` plus a
 * `display.padding` escape hatch and a `className` hatch — both retired here
 * (phase 4's D4: a call site needing `className` has found a missing variant,
 * not a missing hatch). Content is now a tagged union over `layout`, the
 * house idiom `Button`'s `kind` and `Modal`'s `control` also use, so a
 * `"body-only"` card cannot also be handed a `footer` it will never render.
 *
 * Geometry moved to outbound's no-self-padding shape: a caller owns its own
 * padding (inside `body`/`title`/`footer`), `Card` owns only the surface
 * (border, radius, shadow). `variant` folds in the two families phase 7 left
 * out — `.panel` and `.statCard` (§2e) — as shadow/padding/gap arms rather
 * than a `className` override, so the three retired CSS rules become one
 * reviewable set of values instead of three near-duplicates.
 */
export type CardVariant = "flat" | "panel" | "stat" | "stat-warning";

type CardSlots =
  | { layout: "body-only"; body: ReactNode }
  | { layout: "title-body"; title: ReactNode; body: ReactNode }
  | {
      layout: "title-body-footer";
      title: ReactNode;
      body: ReactNode;
      footer: ReactNode;
    };

export type CardProps = CardSlots &
  DataAttributes & {
    /**
     * `"flat"` (default) is the plain surface — border, `out-100` shadow,
     * `12px` radius, no padding — that replaces the outbound T2 `card` class
     * and every existing Engine Room call site. `"panel"` and `"stat"` fold
     * in `.panel` and `.statCard` (SR-301 phase 8 §2e/§5 R5): both are a
     * column stack with their own padding and gap, differing from `"flat"`
     * only in shadow strength, padding and gap.
     */
    variant?: CardVariant;
    style?: CSSProperties;
    /**
     * Makes the whole card one press target (a `role="button"` surface, not a
     * `<button>`, because a card holds block content). Pass `pressLabel` too:
     * the card's contents are no name for a control. Use a link around the
     * card instead when pressing it navigates.
     */
    onPress?: () => void;
    pressLabel?: string;
  };

function Card(props: CardProps) {
  const { layout, variant = "flat", style, onPress, pressLabel } = props;
  const passthrough = dataAttributes(props);
  const pressable = onPress
    ? {
        role: "button",
        tabIndex: 0,
        "aria-label": pressLabel,
        onClick: onPress,
        onKeyDown: (event: KeyboardEvent<HTMLDivElement>) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            onPress();
          }
        },
      }
    : undefined;

  return (
    <div
      {...pressable}
      className={clsx(styles.card, {
        [styles.pressable]: onPress !== undefined,
        [styles.panel]: variant === "panel",
        [styles.stat]: variant === "stat" || variant === "stat-warning",
        [styles.statWarning]: variant === "stat-warning",
      })}
      style={style}
      {...passthrough}
    >
      {layout !== "body-only" ? (
        <div className={styles.title}>{props.title}</div>
      ) : null}
      <div className={styles.body}>{props.body}</div>
      {layout === "title-body-footer" ? (
        <div className={styles.footer}>{props.footer}</div>
      ) : null}
    </div>
  );
}

export default Card;
